/**
 * Pure aggregation for the Overall dashboard's stat cards (dashboard redesign
 * step 2). Every figure here is composed from an existing canonical source,
 * never re-derived:
 *   - PP gross/net revenue, margins, properties count: computeDerivedMetrics()
 *     over the SAME /reports/portfolio/dataset the Property Portfolio
 *     dashboard itself renders (lib/portfolioDerived.js). The caller passes
 *     `pp` ALREADY in the display currency (via convertDerivedToDisplayCurrency,
 *     which re-derives every figure from its native currency) -- this module
 *     applies no further conversion to it, so a French property's EUR-native
 *     revenue is never round-tripped through GBP.
 *   - BR OS turnover/gross margin: /reports/bros/dashboard kpis
 *     (services/bros_revenue.compute_bros_turnover, the single BR OS revenue
 *     source of truth). BR OS has no native EUR breakdown available at this
 *     call site, so EUR display converts its GBP total via the group spot
 *     rate (a real, freshly-fetched rate, not a stale fallback).
 *   - BR OS financing cost: /costs/invoice-financing summary
 *     (services/invoice_financing.compute_invoice_financing).
 *   - Net assets: /al/balance-sheet totals.net_assets, business + personal
 *     scope, summed (intercompany between the two scopes cancels by
 *     construction, so this needs no separate elimination step). The caller
 *     passes `netAssets` ALREADY in the display currency (the endpoint
 *     converts from native via its own display_currency param), same
 *     no-round-trip treatment as PP. If either the business or personal
 *     balance-sheet fetch failed, the caller passes `netAssetsError: true`
 *     rather than letting the failed side silently sum as 0 -- a real zero
 *     and a failed fetch must never render the same "£0" headline.
 *   - Bank balance jurisdiction buckets: bucketAccountsByJurisdiction() over
 *     /bank-balances/summary, the same predicate-driven bucketing
 *     GroupJurisdictionTab and the PP dashboard use; converts natively per
 *     account currency, never via a single blended rate.
 *   - Avg monthly net cashflow: NOT /dashboard/cash-runway's avg_monthly_net
 *     directly (that field is the Cash Runway card's OWN trailing-6-full-
 *     month figure, a different window). The caller computes
 *     computeAvgNetCashflow() over that same endpoint's calendar_year_months
 *     (complete months only -- see monthlyCashflowChart.js) and passes the
 *     result in as `avgMonthlyNetGbp`. The backend converts every
 *     contributing transaction to GBP itself (dashboard.py's
 *     get_cash_runway, target_currency="GBP" for any scope-based/no-entity_id
 *     call) before this module ever sees a number, so `cv()` below is the
 *     ONLY currency conversion applied here -- do not add a second one.
 *     UK/France sublines come from that same endpoint's
 *     avg_monthly_net_by_jurisdiction, GBP for the same reason.
 *   - Net assets fixed/current split: lib/entityStatCards.computeNetAssetsCard
 *     over the SAME /al/balance-sheet responses netAssets sums, business +
 *     personal -- the identical pure function and predicate the entity
 *     dashboard's Net Assets card uses, never a second derivation.
 *   - Properties UK/France split: derived.totals.uk/fr.properties_count from
 *     the SAME computeDerivedMetrics() call the combined properties_count
 *     (and the PP dashboard's own region tabs) already use.
 *
 * Margin tiers (Fan, 2026-11-04, A148), the same four terms for both lines:
 *   Net revenue  BR OS: turnover - supplier cost (kpis total_gross_margin)
 *                PP: gross - OTA, management fees, cleaning, repairs
 *   Gross margin BR OS: net revenue - lease voids (brosLeaseVoidsGbp)
 *                PP: net revenue - fixed property costs (rent, mortgage, ...)
 *   Net margin   BR OS: gross margin - invoice financing - overheads
 *                PP: gross margin - overheads (portfolioDerived.js)
 * `financingCostGbp` is everything between BR OS gross margin and net
 * margin (invoice financing + overheads), read off the BR OS P&L.
 *
 * Actuals-scoped YTD (dashboards round 2, "incl. to come" fix): PP's
 * gross_revenue_actual_gbp/net_revenue_actual_gbp/gross_margin_actual_gbp/
 * net_margin_actual_gbp (portfolioDerived.js) exclude a same-month forward-
 * dated booking (never an actual, regardless of status) -- the same
 * predicate the Gross Revenue chart's YTD footer already reads. BR OS needs
 * no split at all: its query already excludes future dates, so its full
 * total already IS actuals-only, and is used unconditionally for both the
 * headline and the full-year figure. Each of the four cards' headline is
 * now the actuals-scoped sum; the full-year (forward-inclusive) figure
 * survives as `inclToCome` (null when there's nothing forward-looking to
 * show), rendered as a labelled secondary line, never a second percentage.
 */

import { bucketAccountsByJurisdiction } from './jurisdictionBuckets';
import { makeLiveBalanceConverter } from './displayCurrency';

function convGbp(amountGbp, displayCurrency, eurGbpRate) {
  var amt = amountGbp || 0;
  if (displayCurrency === 'GBP') return amt;
  if (!eurGbpRate) return 0;
  return amt / eurGbpRate;
}

export function computeGroupStatCards(input) {
  // pp and netAssets arrive PRE-CONVERTED to displayCurrency by the caller
  // (native-currency conversion, no GBP round-trip) -- see module docstring.
  var pp = input.pp || {};
  var netAssets = input.netAssets || 0;
  var netAssetsFixed = input.netAssetsFixed || 0;
  var netAssetsCurrent = input.netAssetsCurrent || 0;
  var netAssetsError = input.netAssetsError || false;
  var propertiesUk = input.propertiesUk || 0;
  var propertiesFr = input.propertiesFr || 0;
  var brosTurnoverGbp = input.brosTurnoverGbp || 0;
  // BR OS net revenue = turnover - supplier cost; gross margin also after
  // lease voids. Older callers passed only brosGrossMarginGbp (= turnover -
  // supplier cost) and no lease voids: both tiers then coincide.
  var brosNetRevenueGbp = input.brosNetRevenueGbp != null ? input.brosNetRevenueGbp : (input.brosGrossMarginGbp || 0);
  var brosLeaseVoidsGbp = input.brosLeaseVoidsGbp || 0;
  var brosGrossMarginGbp = brosNetRevenueGbp - brosLeaseVoidsGbp;
  var financingCostGbp = input.financingCostGbp || 0;
  var cashNow = input.cashNow || { gbp: 0, eur: 0 };
  var avgMonthlyNetGbp = input.avgMonthlyNetGbp || 0;
  var avgMonthlyNetByJurisdictionGbp = input.avgMonthlyNetByJurisdictionGbp || {};
  var avgMonthlyNetByScopeGbp = input.avgMonthlyNetByScopeGbp || {};
  var bankAccounts = input.bankAccounts || [];
  var fxRatesSpot = input.fxRatesSpot || {};
  var displayCurrency = input.displayCurrency || 'GBP';
  var eurGbpRate = input.eurGbpRate || 0.855;

  var cv = function (gbp) { return convGbp(gbp, displayCurrency, eurGbpRate); };

  var ppGross = pp.gross_revenue_gbp || 0;
  var ppNet = pp.net_revenue_gbp || 0;
  var ppGrossMargin = pp.gross_margin_gbp || 0;
  var ppNetMargin = pp.net_margin_gbp || 0;
  // Actuals-scoped PP figures, falling back to the full-year total when the
  // split field is absent (older fixture) -- fallback makes actual and
  // total coincide exactly when there is nothing to split, never a silent
  // zero.
  var ppGrossActual = pp.gross_revenue_actual_gbp != null ? pp.gross_revenue_actual_gbp : ppGross;
  var ppNetActual = pp.net_revenue_actual_gbp != null ? pp.net_revenue_actual_gbp : ppNet;
  var ppGrossMarginActual = pp.gross_margin_actual_gbp != null ? pp.gross_margin_actual_gbp : ppGrossMargin;
  var ppNetMarginActual = pp.net_margin_actual_gbp != null ? pp.net_margin_actual_gbp : ppNetMargin;

  var brosTurnover = cv(brosTurnoverGbp);
  var brosNetRevenue = cv(brosNetRevenueGbp);
  var brosGrossMargin = cv(brosGrossMarginGbp);
  var brosFinancingCost = cv(financingCostGbp);
  // Combine in GBP first, then convert once, so a single conversion rounding
  // step applies (not one per operand). BR OS needs no actual/forward split:
  // its query already excludes future dates, so its full total is already
  // actuals-only.
  var brosNetMargin = cv(brosGrossMarginGbp - financingCostGbp);

  var grossRevenue = ppGross + brosTurnover;
  // Net revenue (Fan, 2026-11-04, A148): Portfolio after OTA, management
  // fees, cleaning and repairs (ppNet); BR OS after the supplier payment,
  // NOT its turnover (reading it as turnover treated GBP 1.15m of supplier
  // cost as margin, 2026-08-30).
  var netRevenue = ppNet + brosNetRevenue;
  var grossMargin = ppGrossMargin + brosGrossMargin;
  var netMargin = ppNetMargin + brosNetMargin;

  var grossRevenueActual = ppGrossActual + brosTurnover;
  var netRevenueActual = ppNetActual + brosNetRevenue;
  var grossMarginActual = ppGrossMarginActual + brosGrossMargin;
  var netMarginActual = ppNetMarginActual + brosNetMargin;
  // Both percentages are of GROSS revenue (the top-line), per spec -- and
  // now compute on the actuals figures only, never a second percentage for
  // the forward-inclusive figure.
  var grossMarginPct = grossRevenueActual !== 0 ? (grossMarginActual / grossRevenueActual) * 100 : 0;
  var netMarginPct = grossRevenueActual !== 0 ? (netMarginActual / grossRevenueActual) * 100 : 0;

  var inclToCome = function (total, actual) { return total !== actual ? total : null; };

  var avgMonthlyNetCashflow = cv(avgMonthlyNetGbp);
  var avgMonthlyNetUk = cv(parseFloat(avgMonthlyNetByJurisdictionGbp.UK || 0));
  var avgMonthlyNetFr = cv(parseFloat(avgMonthlyNetByJurisdictionGbp.FR || 0));
  var avgMonthlyNetPersonal = cv(parseFloat(avgMonthlyNetByJurisdictionGbp.personal || 0));
  // BR OS / Property Portfolio split, same raw-transaction basis as the
  // headline (dashboard.py's avg_monthly_net_by_scope, bucketed via the
  // canonical _ENTITY_TO_SCOPE map -- never a second scope predicate).
  var avgMonthlyNetBrosOs = cv(parseFloat(avgMonthlyNetByScopeGbp.br_os || 0));
  var avgMonthlyNetPortfolio = cv(parseFloat(avgMonthlyNetByScopeGbp.property_portfolio || 0));

  var bankConvert = makeLiveBalanceConverter(fxRatesSpot);
  var grouped = bucketAccountsByJurisdiction(bankAccounts, {}, {});
  var ukBal = bankConvert((grouped.balByJurisdiction.UK || {}).balances || {}, displayCurrency);
  var frBal = bankConvert((grouped.balByJurisdiction.France || {}).balances || {}, displayCurrency);
  var personalBal = bankConvert((grouped.balByJurisdiction.Personal || {}).balances || {}, displayCurrency);
  var unassignedBal = bankConvert((grouped.unassigned || {}).balances || {}, displayCurrency);
  var cashNowValue = displayCurrency === 'GBP' ? (cashNow.gbp || 0) : (cashNow.eur || 0);

  return {
    grossRevenue: {
      headline: grossRevenueActual, pp: ppGrossActual, brosOs: brosTurnover,
      inclToCome: inclToCome(grossRevenue, grossRevenueActual),
    },
    netRevenue: {
      headline: netRevenueActual, pp: ppNetActual, brosOs: brosNetRevenue,
      inclToCome: inclToCome(netRevenue, netRevenueActual),
    },
    grossMargin: {
      headline: grossMarginActual, pp: ppGrossMarginActual, brosOs: brosGrossMargin, pct: grossMarginPct,
      inclToCome: inclToCome(grossMargin, grossMarginActual),
    },
    netMargin: {
      headline: netMarginActual, pp: ppNetMarginActual, brosOs: brosNetMargin, pct: netMarginPct,
      inclToCome: inclToCome(netMargin, netMarginActual),
    },
    bankBalance: {
      headline: cashNowValue,
      uk: ukBal,
      france: frBal,
      personal: personalBal,
      unassigned: unassignedBal,
    },
    netAssetsCard: { headline: netAssets, fixed: netAssetsFixed, current: netAssetsCurrent, error: netAssetsError },
    propertiesCard: { headline: pp.properties_count || 0, uk: propertiesUk, france: propertiesFr },
    avgMonthlyNetCashflowCard: {
      headline: avgMonthlyNetCashflow,
      uk: avgMonthlyNetUk,
      france: avgMonthlyNetFr,
      personal: avgMonthlyNetPersonal,
      brosOs: avgMonthlyNetBrosOs,
      pp: avgMonthlyNetPortfolio,
    },
    brosFinancingCost: brosFinancingCost,
  };
}
